<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { InputTranslatedComponentProps } from '@shared/common/form/application/component/translated/InputTranslatedComponent.types';
import type { TranslatedTextLocale } from '@shared/common/translation/domain/model/translated-text.model';
import { TRANSLATED_TEXT_LOCALES } from '@shared/common/translation/domain/constant/translated-text-locales.constant';
import type { TranslatedProperty } from '@shared/common/translation/domain/model/translated-property.model';
import RichTextEditorQuill from '@shared/common/form/application/component/rich-text/RichTextEditorQuill.vue';

const model = defineModel<TranslatedProperty | null>({ required: true });
const props = defineProps<InputTranslatedComponentProps>();

const { t } = useI18n();

const locales = TRANSLATED_TEXT_LOCALES;

type Language = { code: TranslatedTextLocale; label: string };
const languages = computed<Language[]>(() =>
  locales.map((code) => ({
    code,
    label: String(t(`common.languages.${code}`)),
  })),
);

const activeTab = ref<TranslatedTextLocale>(locales[0] ?? 'fr');

function buildEmptyTranslatedProperty(): TranslatedProperty {
  return locales.reduce((acc, locale) => ({ ...acc, [locale]: null }), {} as TranslatedProperty);
}

const translatedProperty = computed<TranslatedProperty>(() => model.value ?? buildEmptyTranslatedProperty());

const activeValue = computed<string>({
  get: () => translatedProperty.value[activeTab.value] ?? '',
  set: (value: string) => {
    model.value = {
      ...translatedProperty.value,
      [activeTab.value]: value.length ? value : null,
    };
  },
});
</script>

<template>
  <div class="input-translated-component relative flex flex-col items-start">
    <div class="mb-2 w-full">
      <p class="typo-subtitle1 text-black">
        {{ props.label }}
      </p>
      <p v-if="props.hint" class="mt-0.5 typo-text3 text-grey">
        {{ props.hint }}
      </p>
    </div>

    <div class="w-full">
      <div class="flex flex-wrap gap-6 border-b border-grey-light pb-2">
        <button
          v-for="language in languages"
          :key="language.code"
          v-capture-button-name="`select-translated-field-locale-${language.code}`"
          type="button"
          class="relative -mb-2 border-b-2 pb-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange/40"
          :class="
            language.code === activeTab ? 'border-primary text-black' : 'border-transparent text-grey hover:text-black'
          "
          @click="activeTab = language.code"
        >
          {{ language.label }}
        </button>
      </div>

      <RichTextEditorQuill
        v-if="props.widget === 'rich-text'"
        v-model="activeValue"
        class="mt-3"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :error="props.hasError ? 'error' : undefined"
      />
      <textarea
        v-else-if="props.widget !== 'text'"
        :id="props.id"
        v-model="activeValue"
        v-capture-input-name="`translated-field-${activeTab}`"
        class="mt-3 transition-colors duration-[250ms] w-full border border-solid rounded-sm text-left min-h-[140px] py-3 px-3 outline-none typo-title4 placeholder:typo-text1 placeholder:text-grey focus:border-black"
        :class="[
          {
            'text-black border-grey-light': !props.hasError && !props.disabled,
            'text-app-red-dark border-app-red-dark': !!props.hasError && !props.disabled,
            'bg-grey-light text-grey': props.disabled,
          },
        ]"
        :aria-label="props.label"
        :aria-invalid="props.hasError || undefined"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :placeholder="props.placeholder"
      />
      <input
        v-else
        :id="props.id"
        v-model="activeValue"
        v-capture-input-name="`translated-field-${activeTab}`"
        type="text"
        class="mt-3 transition-colors duration-[250ms] w-full border border-solid rounded-sm text-left h-12 py-4 px-3 outline-none typo-title4 placeholder:typo-text1 placeholder:text-grey focus:border-black"
        :class="{
          'text-black border-grey-light': !props.hasError && !props.disabled,
          'text-app-red-dark border-app-red-dark': !!props.hasError && !props.disabled,
          'bg-grey-light text-grey': props.disabled,
        }"
        :aria-label="props.label"
        :aria-invalid="props.hasError || undefined"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :placeholder="props.placeholder"
      />
    </div>
  </div>
</template>
